import { useTheme } from '@emotion/react';
import { useSetAtom } from 'jotai';
import React, { useCallback } from 'react';

import { Flex } from '~/components/blocks';
import { CancelUnavailableSendMessageWaitingForChargePane } from '~/components/partials/CancelUnavailableSendMessageWaitingForChargePane';
import { CancelWaitingForChargePane } from '~/components/partials/CancelWaitingForChargePane';
import { ActionButton } from '~/components/partials/ReceptionDrawer/ActionButton';
import { InactiveButton } from '~/components/partials/ReceptionDrawer/InactiveButton';
import { LineVisitorAppointmentDetailFragment } from '~/graphql/generated/graphql';
import { cancelPane } from '~/state/partials/cancel_pane/atoms';
import { cancelUnavailableSendMessagePane } from '~/state/partials/cancel_unavailable_send_message_pane/atoms';
import { lineVisitorChargeModalState } from '~/state/reception/atoms';

import { useCancelAppointment } from './use-cancel-appointment';
import { useCancelAppointmentWithoutMessage } from './use-cancel-appointment-without-message';

type Props = {
  appointment: LineVisitorAppointmentDetailFragment;
};

export const WaitingForChargeButtons = (props: Props) => {
  const setChargeModalState = useSetAtom(lineVisitorChargeModalState);
  const setCancelPane = useSetAtom(cancelPane);
  const setCancelUnavailableSendMessagePane = useSetAtom(cancelUnavailableSendMessagePane);
  const theme = useTheme();
  const messageSendable = props.appointment.lineVisitor?.messageSendable ?? false;

  const { canceling, handleCancel } = useCancelAppointment(props.appointment.id);
  const { canceling: unavailableSendCanceling, handleCancel: handleUnavailableSendCancel } =
    useCancelAppointmentWithoutMessage(props.appointment.id);

  // 会計モーダルで確定後
  // - 未会計 -> 会計済(配送待ち) （telemedicine: true）
  // - 指導待ち -> 未会計 -> 完了 （telemedicine: false）
  const openChargeModal = useCallback(() => {
    setChargeModalState({
      isOpen: true,
      appointmentId: props.appointment.id,
    });
  }, [setChargeModalState, props.appointment.id]);

  // キャンセル
  const handleClickCancel = useCallback(() => {
    if (!messageSendable) {
      setCancelUnavailableSendMessagePane({ isOpen: true });
      return;
    }
    setCancelPane({ isOpen: true });
  }, [messageSendable, setCancelPane, setCancelUnavailableSendMessagePane]);

  const isSentCongestionNotification = !!props.appointment.congestionNotificationSentAt;

  return (
    <>
      <Flex flexDirection="column">
        <ActionButton use="primary" wide="fill" size="l" onClick={openChargeModal}>
          会計
        </ActionButton>
        <Flex alignItems="center" marginTop={theme.space.m}>
          <InactiveButton
            status={isSentCongestionNotification ? '送信済' : undefined}
            wide="half"
            size="m"
          >
            混雑時通知を送信
          </InactiveButton>
          <InactiveButton status="送信済" marginLeft={theme.space.m} wide="half" size="m">
            準備完了通知を送信
          </InactiveButton>
        </Flex>
        <ActionButton use="bgGrey" onClick={handleClickCancel} marginTop={theme.space.m} size="s">
          キャンセル
        </ActionButton>
      </Flex>
      <CancelWaitingForChargePane align="center" canceling={canceling} onCancel={handleCancel} />
      <CancelUnavailableSendMessageWaitingForChargePane
        align="center"
        canceling={unavailableSendCanceling}
        onCancel={handleUnavailableSendCancel}
      />
    </>
  );
};
